import { useEffect, useMemo, useState } from 'react';
import {
  analyzeBankAccount,
  registerBankAccount,
  type BankAccountAnalysis,
  type BankAccountDraft,
  type MissingBankAccount,
} from '@/api/payment-prep';
import { toMessage } from '@/api/to-message';
import { OUTLINE_PILL, PRIMARY_PILL } from '@/payment/payment-styles';

/** 読み取り・登録の進み */
type Phase =
  | { step: 'reading' }
  | { step: 'editing'; analysis: BankAccountAnalysis }
  | { step: 'saving'; analysis: BankAccountAnalysis }
  | { step: 'done'; partnerName: string }
  | { step: 'failed'; message: string; analysis?: BankAccountAnalysis };

/**
 * BankAccountDialog の props。
 */
export interface BankAccountDialogProps {
  /** 登録する取引先 */
  partner: MissingBankAccount;
  /** 閉じるときに呼ばれる */
  onClose: () => void;
  /** 登録が終わったときに呼ばれる（一覧から外すため） */
  onRegistered: (partnerId: number) => void;
}

/** 入力欄の並び（ラベル・項目・補足） */
/** @graph-connects none */
const FIELDS: { key: keyof BankAccountDraft; label: string; hint?: string; wide?: boolean }[] = [
  { key: 'bankName', label: '銀行名' },
  { key: 'bankCode', label: '金融機関コード', hint: '4 桁' },
  { key: 'branchName', label: '支店名' },
  { key: 'branchCode', label: '支店コード', hint: '3 桁' },
  { key: 'accountNumber', label: '口座番号', hint: '7 桁以内' },
  { key: 'accountName', label: '口座名義（カナ）', hint: '半角カナ' },
  { key: 'longAccountName', label: '口座名義（漢字）', wide: true },
];

/** @graph-connects none */
const INPUT =
  'w-full rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-3 py-1.5 text-sm text-[var(--color-text-primary)] focus:border-[var(--color-doing-border)] focus:outline-none';

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business base64 の請求書を、画面に出せる一時的な URL にする
 * @graph-connects none
 */
export function toObjectUrl(base64: string, mimeType: string): string {
  const bytes = Uint8Array.from(atob(base64), (ch) => ch.charCodeAt(0));
  return URL.createObjectURL(new Blob([bytes], { type: mimeType }));
}

/**
 * @graph-node {Page}
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 取引先の請求書を読み取り、左に請求書・右に振込先口座の下書きを並べて人が確認・修正し、freee に登録するポップアップ。確認が要る点は上に出す。既に口座がある取引先は API 側で断られる
 * @graph-connects analyzeBankAccount [calls] 請求書を読み取る
 * @graph-connects registerBankAccount [calls] freee に登録する
 */
export function BankAccountDialog({ partner, onClose, onRegistered }: BankAccountDialogProps) {
  const [receiptId, setReceiptId] = useState(partner.receiptIds[0] ?? '');
  const [phase, setPhase] = useState<Phase>({ step: 'reading' });
  const [draft, setDraft] = useState<BankAccountDraft | null>(null);

  // 証憑を選び直すたびに読み取る
  useEffect(() => {
    let cancelled = false;
    setPhase({ step: 'reading' });
    analyzeBankAccount(receiptId)
      .then((analysis) => {
        if (!cancelled) {
          setDraft(analysis.draft);
          setPhase({ step: 'editing', analysis });
        }
      })
      .catch((cause: unknown) => {
        if (!cancelled) {
          setPhase({ step: 'failed', message: toMessage(cause) });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [receiptId]);

  const analysis = 'analysis' in phase ? phase.analysis : undefined;
  const previewUrl = useMemo(
    () => (analysis === undefined ? '' : toObjectUrl(analysis.file.base64, analysis.file.mimeType)),
    [analysis],
  );
  useEffect(
    () => () => {
      if (previewUrl !== '') {
        URL.revokeObjectURL(previewUrl);
      }
    },
    [previewUrl],
  );

  const save = async () => {
    if (draft === null || analysis === undefined) {
      return;
    }
    setPhase({ step: 'saving', analysis });
    try {
      const result = await registerBankAccount(partner.partnerId, draft);
      setPhase({ step: 'done', partnerName: result.partnerName || partner.partnerName });
      onRegistered(partner.partnerId);
    } catch (cause: unknown) {
      setPhase({ step: 'failed', message: toMessage(cause), analysis });
    }
  };

  const busy = phase.step === 'reading' || phase.step === 'saving';

  return (
    <>
      <button
        type="button"
        aria-label="閉じる"
        disabled={busy}
        onClick={onClose}
        className="fixed inset-0 z-10 cursor-default bg-[var(--color-text-primary)]/20"
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={`${partner.partnerName} の振込先口座`}
        className="fixed top-1/2 left-1/2 z-20 flex max-h-[90vh] w-[min(64rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 flex-col rounded-3xl border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-7 py-6 shadow-[0_12px_40px_var(--color-shadow)]"
      >
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <h2 className="m-0 text-base font-semibold text-[var(--color-text-primary)]">
            {partner.partnerName}
          </h2>
          <span className="text-xs text-[var(--color-text-muted)]">振込先口座を freee に登録</span>
          {partner.receiptIds.length > 1 ? (
            <label className="ml-auto flex items-center gap-2 text-xs text-[var(--color-text-secondary)]">
              読み取る請求書
              <select
                aria-label="読み取る請求書"
                value={receiptId}
                disabled={busy}
                onChange={(event) => {
                  setReceiptId(event.target.value);
                }}
                className="rounded-full border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-3 py-1 text-xs"
              >
                {partner.receiptIds.map((id) => (
                  <option key={id} value={id}>
                    証憑 {id}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>

        {phase.step === 'reading' ? (
          <p
            role="status"
            className="m-0 py-10 text-center text-sm text-[var(--color-text-secondary)]"
          >
            請求書を読み取っています…（10〜30 秒ほど）
          </p>
        ) : null}

        {phase.step === 'done' ? (
          <div role="status" className="py-6 text-center">
            <p className="m-0 text-sm text-[var(--color-text-primary)]">
              {phase.partnerName} の振込先口座を freee に登録しました
            </p>
            <button type="button" onClick={onClose} className={`${PRIMARY_PILL} mt-4`}>
              閉じる
            </button>
          </div>
        ) : null}

        {phase.step === 'failed' && analysis === undefined ? (
          <div className="py-6 text-center">
            <p role="alert" className="m-0 text-sm text-[var(--color-overdue)]">
              {phase.message}
            </p>
            <button type="button" onClick={onClose} className={`${OUTLINE_PILL} mt-4`}>
              閉じる
            </button>
          </div>
        ) : null}

        {analysis !== undefined && draft !== null ? (
          <div className="grid min-h-0 flex-1 gap-5 overflow-y-auto md:grid-cols-2">
            <div className="min-h-[24rem] overflow-hidden rounded-2xl border border-[var(--color-border-subtle)] bg-[var(--color-bg-subtle)]">
              {analysis.file.mimeType.startsWith('image/') ? (
                <img src={previewUrl} alt="請求書" className="h-full w-full object-contain" />
              ) : (
                <iframe title="請求書" src={previewUrl} className="h-full min-h-[24rem] w-full" />
              )}
            </div>

            <div className="flex flex-col gap-3">
              {phase.step === 'failed' ? (
                <p
                  role="alert"
                  className="m-0 rounded-2xl bg-[var(--color-kind-closing-bg)] px-4 py-2 text-sm text-[var(--color-overdue)]"
                >
                  {phase.message}
                </p>
              ) : null}
              {analysis.warnings.length === 0 ? (
                <p className="m-0 rounded-2xl bg-[var(--color-kind-meeting-bg)] px-4 py-2 text-sm text-[var(--color-kind-meeting-text)]">
                  読み取れました。請求書と見比べてから登録してください
                </p>
              ) : (
                <ul className="m-0 list-none space-y-1 rounded-2xl bg-[var(--color-kind-closing-bg)] px-4 py-2 text-sm text-[var(--color-kind-closing-text)]">
                  {analysis.warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              )}

              <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                {FIELDS.map((field) => (
                  <label
                    key={field.key}
                    className={`flex flex-col gap-1 text-xs text-[var(--color-text-secondary)] ${field.wide === true ? 'col-span-2' : ''}`}
                  >
                    <span>
                      {field.label}
                      {field.hint === undefined ? null : (
                        <span className="ml-1 text-[var(--color-text-muted)]">{field.hint}</span>
                      )}
                    </span>
                    <input
                      aria-label={field.label}
                      value={draft[field.key]}
                      disabled={phase.step === 'saving'}
                      onChange={(event) => {
                        setDraft({ ...draft, [field.key]: event.target.value });
                      }}
                      className={INPUT}
                    />
                  </label>
                ))}
                <label className="flex flex-col gap-1 text-xs text-[var(--color-text-secondary)]">
                  口座種別
                  <select
                    aria-label="口座種別"
                    value={draft.accountType}
                    disabled={phase.step === 'saving'}
                    onChange={(event) => {
                      setDraft({
                        ...draft,
                        accountType: event.target.value === 'checking' ? 'checking' : 'ordinary',
                      });
                    }}
                    className={INPUT}
                  >
                    <option value="ordinary">普通</option>
                    <option value="checking">当座</option>
                  </select>
                </label>
              </div>

              <p className="m-0 text-xs leading-relaxed text-[var(--color-text-muted)]">
                登録できるのは振込先口座が空の取引先だけです（既にある口座は書き換えません）。
                口座の登録は誤振込につながるので、請求書と一字ずつ見比べてください。
              </p>

              <div className="mt-auto flex justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={phase.step === 'saving'}
                  className={OUTLINE_PILL}
                >
                  取りやめる
                </button>
                <button
                  type="button"
                  disabled={phase.step === 'saving'}
                  onClick={() => {
                    void save();
                  }}
                  className={PRIMARY_PILL}
                >
                  {phase.step === 'saving' ? '登録しています…' : 'freee に登録する'}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </aside>
    </>
  );
}
